import React from 'react';
import { ContractInfo, HotTokensRowInfo } from 'api/types/httpsTypes/contracts';
import { NotificationDex } from '../components/alerts/notification';
import {
  LoadingTerminalDesktop,
  LoadingTerminalMobile,
} from '../terminal/components/loadingComponent';
import { useUserState } from 'modules/user/UserContext';
import { ChevronLeftIcon } from '@heroicons/react/24/outline';
import { twMerge } from 'tailwind-merge';
import { MainInfoComponent } from '../terminal/stats/main-info';
import { screenGte, useMediaQuery } from 'modules/media/use-media-query';
import { useContractInfo } from 'modules/routing/mainSettingsProvider';

interface Props {
  children: React.ReactNode;
  contract: ContractInfo | undefined;
  hotTokens: HotTokensRowInfo[] | undefined;
  isLoadingTemplates?: boolean;
  isLoading: boolean;
  error: any;
}

export function TerminalPageNew({
  hotTokens,
  contract,
  children,
  isLoading,
  error,
  isLoadingTemplates,
}: Props) {
  const { user } = useUserState();
  const { isMainInfoOpen: isSidebarOpen, setIsMainInfoOpen: setIsSidebarOpen } =
    useContractInfo();

  const isDesktop = useMediaQuery(screenGte.large);

  if (isLoading || (user && isLoadingTemplates)) {
    if (isDesktop) {
      return <LoadingTerminalDesktop isSidebarOpen={isSidebarOpen} />;
    } else {
      return <LoadingTerminalMobile />;
    }
  }

  if (error || !contract) {
    return (
      <div className="xxs:m-2">
        <NotificationDex errorMessage={error} type="error">
          Could not load contract data.
        </NotificationDex>
      </div>
    );
  }

  return (
    <div className="space-y-0 pb-16 lg:pb-0 lg:space-y-1">
      <div
        className={twMerge(
          'lg:grid relative transition-all duration-300 ',
          isSidebarOpen ? 'lg:grid-cols-6' : 'lg:grid-cols-1'
        )}
      >
        {/* Sidebar */}
        {isSidebarOpen && (
          <div
            className={twMerge(
              'relative transition-all duration-300 lg:sticky lg:top-0 lg:overflow-y-auto lg:h-terminal border-tailwind xxs:border xxs:border-b-0 xxs:border-x-0 xxs:border-dex-black-700 transform',
              isSidebarOpen ? 'translate-x-0' : '-translate-x-full'
            )}
          >
            <MainInfoComponent hotTokens={hotTokens} contract={contract} />
          </div>
        )}

        {/* Toggle Button */}
        <button
          onClick={() => setIsSidebarOpen(!isSidebarOpen)}
          className={`absolute top-1/2 bg-blue-500 hover:bg-blue-600 text-white w-2 h-10 rounded-r flex items-center justify-center transition-all duration-300
    ${isSidebarOpen ? 'left-[16.6667%]' : 'left-0'}
    -translate-y-1/2`}
          aria-label="Toggle Sidebar"
        >
          <ChevronLeftIcon
            className={`h-4 w-4 transition-transform duration-300 ${
              isSidebarOpen ? 'rotate-0' : 'rotate-180'
            }`}
          />
        </button>

        {/* Main Content */}
        <div
          className={twMerge(
            'flex flex-col w-full  border-tailwind xxs:border xxs:border-dex-black-700',
            isSidebarOpen ? 'col-span-5' : 'col-span-1'
          )}
        >
          {children}
        </div>
      </div>
    </div>
  );
}
